import { useEffect, useState, useRef } from "react";
// Aplica vários setState num único render (React 16/17 não agrupa updates
// disparados depois de um await) — evita o painel piscar com dados parciais.
import { unstable_batchedUpdates } from "react-dom";
import { Modal } from "@material-ui/core";
import { apiEmbalei, apiIDW } from "../../Services/api";
import useStyle from "./style";

// ─────────────────────────────────────────────────────────────────────────────
// HISTORY PANEL — lista de datas agrupadas em 2 colunas
// ─────────────────────────────────────────────────────────────────────────────
const HistoryPanel = ({ data = [], styleHistory, onDayClick }) => {
  const renderRow = (h) => (
    <p
      key={h.date}
      onClick={onDayClick ? () => onDayClick(h.date) : undefined}
      style={onDayClick ? { cursor: "pointer" } : undefined}
      title={onDayClick ? "Ver pedidos do dia" : undefined}
    >
      <span>{h.date}</span>
      <span>({h.items} | {h.orders})</span>
    </p>
  );
  return (
    <div className={styleHistory}>
      <div>{data.slice(0, 6).map(renderRow)}</div>
      <div>{data.slice(6, 12).map(renderRow)}</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────────────────────────────────────────

/** Agrupa array de pedidos por dia (dd/mm). */
const groupByDay = (orders = []) => {
  const map = {};
  orders.forEach((order) => {
    const rawDate = (order.Recordtimestamp || order.NfedhEmi || "").split("T")[0];
    if (!rawDate) return;
    const [, mes, dia] = rawDate.split("-");
    const key = `${dia}/${mes}`;
    if (!map[key]) map[key] = { date: key, sortKey: rawDate, items: 0, orders: 0 };
    map[key].orders += 1;
    map[key].items += parseInt(order.QuantityProduct) || 0;
  });
  return Object.values(map)
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map(({ sortKey, ...rest }) => rest);
};

// O Embalei não tem consulta em lote: a única forma de saber se um pedido foi
// embalado é /orders/:id, um por vez. Com ~1250 pedidos em status 22 isso
// dominava o carregamento inteiro (~52s), então são dois ajustes:
//
//   1. Lote de 50 em paralelo. Medido: 10 em paralelo levam ~410ms e 60 levam
//      ~520ms — o lote pequeno desperdiçava paralelismo à toa.
//   2. Cache dos já embalados entre ciclos (packedOrderIds). Pedido embalado
//      não desembala, então só os NÃO embalados voltam a ser consultados.
//      Hoje ~68% estão embalados, então o ciclo seguinte consulta ~1/3.
const PACKING_BATCH_SIZE = 50;

/**
 * Separa os pedidos entre embalados e não embalados.
 * `packedCache` é um Set de IDOrder mutado aqui — sobrevive entre ciclos.
 */
const splitByPacking = async (orders = [], packedCache) => {
  const pending = orders.filter((o) => !packedCache.has(o.IDOrder));

  for (let i = 0; i < pending.length; i += PACKING_BATCH_SIZE) {
    const batch = pending.slice(i, i + PACKING_BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map((order) => apiEmbalei.get(`/orders/${order.IDOrder}`))
    );
    results.forEach((result, idx) => {
      // Sem dados (array vazio ou erro) → ainda não foi embalado
      const isPacked =
        result.status === "fulfilled" &&
        result.value?.data &&
        (Array.isArray(result.value.data) ? result.value.data.length > 0 : true);
      if (isPacked) packedCache.add(batch[idx].IDOrder);
    });
  }

  // Poda: pedido que saiu do status 22 (embarcou) não precisa mais do cache
  const currentIds = new Set(orders.map((o) => o.IDOrder));
  packedCache.forEach((id) => {
    if (!currentIds.has(id)) packedCache.delete(id);
  });

  return {
    packed: orders.filter((o) => packedCache.has(o.IDOrder)),
    notPacked: orders.filter((o) => !packedCache.has(o.IDOrder)),
  };
};

/** Formata hora do faturamento como "HH:00". */
const toHourKey = (nfeDateStr) => {
  const h = nfeDateStr.split("T")[1]?.split(":")[0];
  return h ? `${h}:00` : null;
};

// ── Faixas do hora-a-hora ────────────────────────────────────────────────────
// Com o terceiro turno o movimento passou a cobrir as 24 horas. A tabela antes
// cortava em 9 linhas por coluna e escondia o resto; agora são duas faixas.
// Metades exatas do dia, 12 linhas cada — as duas faixas ficam do mesmo
// tamanho e a tabela não muda de proporção ao trocar.
const HOUR_TABS = [
  { key: "primeira", from: 0, to: 11 },
  { key: "segunda", from: 12, to: 23 },
];

/** De quanto em quanto tempo a tabela troca de faixa na TV. */
const HOUR_TAB_ROTATE_MS = 15000;

// Altura fixa de cada linha. Sem isso as linhas dividiriam a altura do
// quadrante entre si: de manhã, com três horas no quadro, cada linha ocuparia
// um terço do card e a tabela mudaria de tamanho a cada hora que entrasse.
//
// 6,5% × as 12 linhas da faixa = 78% da área, deixando uma folga embaixo —
// é um valor escolhido a olho, não o 100%/linhas que ocuparia tudo. Se as
// faixas de HOUR_TABS mudarem de tamanho, este número precisa ser revisto
// junto, senão a tabela passa a sobrar ou a cortar.
const HOUR_ROW_HEIGHT = `6.5%`;

// Quantas horas entram na média que projeta a próxima.
const FORECAST_WINDOW_HOURS = 3;

/**
 * Previsão da próxima hora: média das últimas FORECAST_WINDOW_HOURS horas
 * JÁ FECHADAS.
 *
 * Duas decisões que mudam o número:
 *
 * - Janela recente, não o dia inteiro. A média do dia já está na tela (TOTAL
 *   /H) e repetir não ajuda ninguém; o que prevê a próxima hora é o ritmo
 *   agora, e troca de turno, almoço e pico de fim de dia somem numa média de
 *   24h.
 * - A hora em curso fica de fora. Às 10:05 a hora 10 tem cinco minutos de
 *   dado, e entrar na média derrubaria a previsão toda hora virada.
 *
 * Hora fechada sem movimento conta como zero: se a operação parou, a previsão
 * tem que cair junto. Antes da primeira hora fechar não há o que projetar.
 */
const buildForecast = (rows = [], currentHour) => {
  const byHour = {};
  rows.forEach((row) => {
    byHour[row.hour] = row;
  });

  let items = 0;
  let orders = 0;
  let closedHours = 0;
  for (
    let hour = currentHour - 1;
    hour >= 0 && closedHours < FORECAST_WINDOW_HOURS;
    hour -= 1
  ) {
    const row = byHour[`${String(hour).padStart(2, "0")}:00`];
    items += row?.items || 0;
    orders += row?.orders || 0;
    closedHours += 1;
  }

  return {
    label: `${String((currentHour + 1) % 24).padStart(2, "0")}H`,
    items: closedHours ? Math.round(items / closedHours) : "-",
    orders: closedHours ? Math.round(orders / closedHours) : "-",
  };
};

/**
 * Agrupa pedidos por hora, somando peças e pedidos.
 * `getTimestamp` diz qual campo define a hora — emissão da NF no faturamento,
 * data de envio nos embarcados.
 */
const buildHourByHour = (orders = [], getTimestamp) => {
  const byHour = {};
  orders.forEach((order) => {
    const key = toHourKey(getTimestamp(order) || "");
    if (!key) return;
    const quantity = parseInt(order.QuantityProduct) || 0;
    if (byHour[key]) {
      byHour[key].items += quantity;
      byHour[key].orders += 1;
    } else {
      byHour[key] = { hour: key, items: quantity, orders: 1 };
    }
  });
  return Object.values(byHour).sort((a, b) => a.hour.localeCompare(b.hour));
};

/** Linhas de hora-a-hora que caem dentro da faixa (só as que tiveram movimento). */
const rowsInHourTab = (rows, tab) =>
  (rows || []).filter((row) => {
    const hour = parseInt(String(row.hour).slice(0, 2), 10);
    return Number.isFinite(hour) && hour >= tab.from && hour <= tab.to;
  });

/**
 * Completa a faixa com TODAS as horas, não só as que tiveram movimento — a
 * tabela já abre no tamanho final em vez de ganhar linha a cada hora.
 *
 * Hora já vencida sem registro entra zerada (passou e não produziu); hora que
 * ainda não chegou entra marcada como pendente, e o render mostra "-". São
 * coisas diferentes e a TV precisa distinguir.
 */
const fillHourTab = (rows, tab, currentHour) => {
  const byHour = {};
  rowsInHourTab(rows, tab).forEach((row) => {
    byHour[row.hour] = row;
  });

  const filled = [];
  for (let hour = tab.from; hour <= tab.to; hour += 1) {
    const key = `${String(hour).padStart(2, "0")}:00`;
    filled.push(
      byHour[key] || { hour: key, items: 0, orders: 0, pending: hour > currentHour }
    );
  }
  return filled;
};

/** Soma QuantityProduct de um array de pedidos. */
const sumItems = (orders) =>
  orders.reduce((s, r) => s + (parseInt(r.QuantityProduct) || 0), 0);

// ── Tipos de pedido que contam como VENDA (valor em R$) ─────────────────────
const SALE_TYPES = new Set([
  "Amazon DBA", "Vtex", "Mercado Livre Padrão 03",
  "Mercado Livre Flex 03", "WhatsApp", "Shopee",
  "Shopee Flex", "Tiktok",
]);

/** Valor de venda do pedido (0 quando o tipo não é venda). */
const orderValue = (order) =>
  SALE_TYPES.has(order?.TypeOrder) ? parseFloat(order.ValueOrder) || 0 : 0;

/** Soma o valor de venda de um array de pedidos. */
const sumValue = (orders) => orders.reduce((s, o) => s + orderValue(o), 0);

/** Date → "YYYY-MM-DD" no fuso local (sem conversão para UTC). */
const toLocalISO = (date) =>
  new Date(date - date.getTimezoneOffset() * 60000).toISOString().split("T")[0];

// Tipos de pedido usados nas consultas de faturamento
const BILLING_ORDER_TYPES = "4410,4412,871,4041,5440,4293,890,875,877,2967";

// Tipos de pedido da expedição — precisa ser a MESMA lista do Dashboard de
// Embarque, senão pedidos válidos ficam de fora dos quadrantes.
const EXPEDITION_ORDER_TYPES =
  "2967,4410,4412,954,1153,4041,5440,4293,890,875,877,871";
// Intervalo de atualização do painel (5 minutos)
const REFRESH_MS = 300000;
// A API /orders devolve no máximo PAGE_SIZE por página
const PAGE_SIZE = 2000;
const MAX_PAGES = 15;

// ── Fila de requisições da IDWorks ───────────────────────────────────────────
// A API responde "too many requests" quando várias consultas saem juntas, e o
// bloco da meta dispara paginadas em paralelo. TODA chamada passa por esta
// fila, que as serializa e espaça em IDW_REQUEST_DELAY_MS.
const IDW_REQUEST_DELAY_MS = 500;
let idwRequestQueue = Promise.resolve();

const idwGetOrders = (params) => {
  const request = idwRequestQueue.then(async () => {
    const response = await apiIDW.get("/orders", { params });
    await new Promise((resolve) => setTimeout(resolve, IDW_REQUEST_DELAY_MS));
    return response;
  });
  // A fila não pode parar por causa de uma chamada que falhou
  idwRequestQueue = request.catch(() => {});
  return request;
};

/**
 * GET /orders paginado — a API corta em PAGE_SIZE por página. Sem isso, uma
 * consulta grande (ex.: vários dias de faturamento) trunca silenciosamente.
 * Retorna deduplicado por IDOrder.
 */
const fetchOrdersPaged = async (params) => {
  const byId = new Map();
  for (let page = 0; page < MAX_PAGES; page++) {
    const res = await idwGetOrders({ ...params, Page: page });
    const data = Array.isArray(res.data) ? res.data : [];
    if (data.length === 0) break;

    let novos = 0;
    data.forEach((o) => {
      const key = o?.IDOrder;
      if (key != null && !byId.has(key)) {
        byId.set(key, o);
        novos += 1;
      }
    });
    // Backend repetiu a página (ignorou `Page`) ou acabou
    if (novos === 0 || data.length < PAGE_SIZE) break;
  }
  return [...byId.values()];
};

/** Chave de dia (dd/mm) de um pedido — mesma lógica do groupByDay. */
const dayKeyOf = (order) => {
  const rawDate = (order.Recordtimestamp || order.NfedhEmi || "").split("T")[0];
  if (!rawDate) return null;
  const [, mes, dia] = rawDate.split("-");
  return `${dia}/${mes}`;
};

/** Filtra os pedidos de um dia específico (dd/mm). */
const ordersForDay = (orders = [], dayKey) =>
  orders.filter((o) => dayKeyOf(o) === dayKey);

/** "YYYY-MM-DDTHH:mm:ss" → "dd/MM/yyyy HH:mm" (sem conversão de fuso). */
const fmtDateTime = (s) => {
  if (!s) return "—";
  const [date, time] = String(s).split("T");
  const [y, m, d] = date.split("-");
  if (!d) return s;
  return `${d}/${m}/${y}${time ? ` ${time.slice(0, 5)}` : ""}`;
};

/** "YYYY-MM-DD" → "dd/MM/yyyy". */
const fmtDate = (s) => {
  if (!s) return "—";
  const [date] = String(s).split("T");
  const [y, m, d] = date.split("-");
  if (!d) return s;
  return `${d}/${m}/${y}`;
};

// Cores por quadrante (mesmas referências de AG. FATURAMENTO / AG. EMBALAGEM)
const MODAL_THEME = {
  billing: { bg: "#d88889", bar: "#cb6566" },
  packing: { bg: "#82ad82", bar: "#679966" },
};

// ─────────────────────────────────────────────────────────────────────────────
// MODAL — lista os pedidos de um dia do histórico (AG. FATURAMENTO / EMBALAGEM)
// ─────────────────────────────────────────────────────────────────────────────
const HistoryOrdersModal = ({ modal, onClose }) => {
  const t = MODAL_THEME[modal.variant] || MODAL_THEME.billing;
  const th = {
    textAlign: "left",
    padding: "10px 16px",
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    background: t.bar,
    color: "#fff",
  };
  const td = {
    padding: "8px 16px",
    borderBottom: "1px solid #eee",
    color: "#333",
    fontSize: 13,
  };
  return (
    <Modal open={modal.active} onClose={onClose}>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(840px, 94vw)",
          maxHeight: "84vh",
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <div
          style={{
            background: t.bg,
            color: "#fff",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
              {modal.title}
            </div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              {modal.orders.length} pedido{modal.orders.length === 1 ? "" : "s"}
            </div>
          </div>
          <span
            onClick={onClose}
            style={{ cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4 }}
            title="Fechar"
          >
            ✕
          </span>
        </div>

        <div style={{ overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Pedido</th>
                <th style={th}>Transportadora</th>
                <th style={th}>Status</th>
                <th style={th}>Data Faturamento</th>
                <th style={th}>Data Entrega</th>
              </tr>
            </thead>
            <tbody>
              {modal.orders.map((o, i) => (
                <tr
                  key={o.IDOrder != null ? o.IDOrder : i}
                  style={{ background: i % 2 ? "#f6f6f6" : "#fff" }}
                >
                  <td style={td}>{o.Order != null ? o.Order : "—"}</td>
                  <td style={td}>{o.SupplierNameCorporateName || "—"}</td>
                  <td style={td}>{o.StatusOrder != null ? o.StatusOrder : "—"}</td>
                  <td style={td}>{fmtDateTime(o.NfedhEmi)}</td>
                  <td style={td}>{fmtDate(o.ShippingEstimateDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {modal.orders.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
              Nenhum pedido para este dia.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
function ExpeditionV3() {

  // Só renderiza o painel depois do primeiro ciclo completo — evita exibir
  // quadrantes zerados antes dos dados reais chegarem.
  const [loaded, setLoaded] = useState(false);

  // ── Controle de rotação das tabelas com mais de 9 linhas ───────────────────
  // Faixa de hora exibida em cada quadrante (índice em HOUR_TABS). É tela de
  // TV: alterna sozinha, sem controle na tela.
  const [billingHourTab, setBillingHourTab] = useState(0);
  const [packingHourTab, setPackingHourTab] = useState(0);
  const [shippedHourTab, setShippedHourTab] = useState(0);

  // ── Estado: AG. INÍCIO ────────────────────────────────────────────────────
  const [awaiting, setAwaiting] = useState({ total: 0, items: 0, data: [] });

  // ── Estado: AUTORIZADO ────────────────────────────────────────────────────
  const [authorized, setAuthorized] = useState({ total: 0, items: 0, data: [] });

  // ── Estado: ESP. ETIQUETA (Em espera etiqueta) ────────────────────────────
  const [awaitingLabel, setAwaitingLabel] = useState({ total: 0, items: 0, data: [] });

  // ── Estado: FATURAMENTO ───────────────────────────────────────────────────
  const [billing, setBilling] = useState({
    totalOrders: 0,
    totalItems: 0,
    totalValue: 0,
    hourByHour: [],
    data: [],
  });

  // ── Estado: EMBALAGEM ─────────────────────────────────────────────────────
  const [packing, setPacking] = useState({
    averageItems: 0,
    averageOrders: 0,
    totalOrders: 0,
    totalItems: 0,
    hourByHour: [],
  });

  // ── Estado: AG. EMBALAGEM (status 22 ainda NÃO embalados) ─────────────────
  const [awaitingPacking, setAwaitingPacking] = useState({
    total: 0,
    items: 0,
    data: [],
  });

  // ── Estado: EMBARCADOS (enviados hoje, hora a hora) ───────────────────────
  const [shipped, setShipped] = useState({
    totalOrders: 0,
    totalItems: 0,
    hourByHour: [],
    data: [],
  });

  // ── Estado: AG. EMBARCAR (status 22 JÁ embalados) ─────────────────────────
  const [awaitingShipping, setAwaitingShipping] = useState({
    total: 0,
    items: 0,
    data: [],
  });

  // Pedidos já embalados, preservados entre ciclos — ver splitByPacking
  const packedOrderIdsRef = useRef(new Set());

  // ── Estado: modal de pedidos do histórico ─────────────────────────────────
  const [historyModal, setHistoryModal] = useState({
    active: false,
    variant: "billing",
    title: "",
    orders: [],
  });

  const openDayModal = (variant, title, orders) =>
    setHistoryModal({ active: true, variant, title, orders });
  const closeDayModal = () =>
    setHistoryModal((m) => ({ ...m, active: false }));

  // ── Rotação automática das faixas de hora ──────────────────────────────────
  // Alterna sozinha entre as duas faixas a cada HOUR_TAB_ROTATE_MS, e só quando
  // as duas têm movimento — senão ficaria meio minuto numa tabela vazia.
  const billingHasBothTabs = HOUR_TABS.every(
    (tab) => rowsInHourTab(billing.hourByHour, tab).length > 0
  );
  const packingHasBothTabs = HOUR_TABS.every(
    (tab) => rowsInHourTab(packing.hourByHour, tab).length > 0
  );
  const shippedHasBothTabs = HOUR_TABS.every(
    (tab) => rowsInHourTab(shipped.hourByHour, tab).length > 0
  );

  useEffect(() => {
    if (!billingHasBothTabs) return;
    const id = setInterval(
      () => setBillingHourTab((prev) => (prev + 1) % HOUR_TABS.length),
      HOUR_TAB_ROTATE_MS
    );
    return () => clearInterval(id);
  }, [billingHasBothTabs]);

  useEffect(() => {
    if (!packingHasBothTabs) return;
    const id = setInterval(
      () => setPackingHourTab((prev) => (prev + 1) % HOUR_TABS.length),
      HOUR_TAB_ROTATE_MS
    );
    return () => clearInterval(id);
  }, [packingHasBothTabs]);

  useEffect(() => {
    if (!shippedHasBothTabs) return;
    const id = setInterval(
      () => setShippedHourTab((prev) => (prev + 1) % HOUR_TABS.length),
      HOUR_TAB_ROTATE_MS
    );
    return () => clearInterval(id);
  }, [shippedHasBothTabs]);

  /** Tabela hora-a-hora da faixa de turno ativa. */
  const renderHourTable = (rows, activeTabIndex) => {
    const visible = fillHourTab(
      rows,
      HOUR_TABS[activeTabIndex],
      new Date().getHours()
    );

    return (
      <div className={style.boxTable}>
        <div className="scaleTitle">
          <span>HORA</span>
          <span>PEÇAS</span>
          <span>PEDIDOS</span>
          <span>MEDIA</span>
        </div>
        <div className="boxScale">
          <div className="scale">
            {visible.map((h) => (
              <p
                key={h.hour}
                className={h.pending ? "pendingHour" : undefined}
                style={{ height: HOUR_ROW_HEIGHT }}
              >
                <span>{h.hour}</span>
                <span>{h.pending ? "-" : h.items}</span>
                <span>{h.pending ? "-" : h.orders}</span>
                <span>{h.orders > 0 ? (h.items / h.orders).toFixed(0) : "-"}</span>
              </p>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── Dados derivados: históricos agrupados por dia ──────────────────────────
  // AG. FATURAMENTO = lógica anterior do "Caixa": Autorizado + Ag.Início
  const historyBillingAwaiting = groupByDay([
    ...authorized.data,
    ...awaiting.data,
  ]);
  // AG. EMBALAGEM = pedidos do dia já faturados mas ainda não embalados
  const historyPackingAwaiting = groupByDay(awaitingPacking.data);
  // AG. EMBARCAR = status 22 já embalados, esperando a saída do romaneio
  const historyAwaitingShipping = groupByDay(awaitingShipping.data);

  // ── Busca principal de dados ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;     // evita setState após desmontar
    let running = false;       // evita ciclos sobrepostos (splitByPacking é lento)
    let firstLoadDone = false; // 1ª carga publica mesmo com falha (senão trava no loading)

    const fetchAll = async () => {
      const now = new Date();
      const todayISO = toLocalISO(now);

      // Resultado de cada bloco — nada é publicado na tela antes do fim, para
      // o painel trocar de uma vez só (sem passar por valores parciais).
      let nextAwaiting = null;
      let nextAuthorized = null;
      let nextAwaitingLabel = null;
      let nextBilling = null;
      let nextPacking = null;
      let nextAwaitingPacking = null;
      let nextShipped = null;
      let nextAwaitingShipping = null;

      // Consultas que, se falharem, deixam o ciclo incompleto. Nesse caso o
      // ciclo inteiro é descartado e a tela mantém os dados anteriores.
      const failures = [];

      // 1. STATUS PENDENTES — UMA consulta só ──────────────────────────────────
      // A IDW aceita múltiplos status no mesmo IDStatusOrder (verificado: a
      // resposta traz os quatro e bate com a soma das consultas separadas).
      // Antes eram 4 chamadas; agora é uma, paginada — hoje já são ~1970
      // registros, colado no teto de 2000 por página.
      let ordersByStatus = { 1: [], 20: [], 22: [], 23: [] };
      try {
        const data = await fetchOrdersPaged({ IDStatusOrder: "1,20,22,23" });
        data.forEach((order) => {
          const bucket = ordersByStatus[Number(order.IDStatusOrder)];
          if (bucket) bucket.push(order);
        });

        nextAwaiting = {
          total: ordersByStatus[1].length,
          items: sumItems(ordersByStatus[1]),
          data: ordersByStatus[1],
        };
        nextAuthorized = {
          total: ordersByStatus[20].length,
          items: sumItems(ordersByStatus[20]),
          data: ordersByStatus[20],
        };
        nextAwaitingLabel = {
          total: ordersByStatus[23].length,
          items: sumItems(ordersByStatus[23]),
          data: ordersByStatus[23],
        };
      } catch (e) {
        console.error("Erro STATUS PENDENTES:", e);
        failures.push("STATUS_PENDENTES");
      }


      // 2. FATURAMENTO (hoje) ────────────────────────────────────────────────
      try {
        const invoicedToday = await fetchOrdersPaged({
          InvoiceDateFrom: todayISO,
          InvoiceDateTo: todayISO,
          IDOrderType: BILLING_ORDER_TYPES,
        });

        nextBilling = {
          totalItems: sumItems(invoicedToday),
          totalOrders: invoicedToday.length,
          totalValue: sumValue(invoicedToday),
          hourByHour: buildHourByHour(invoicedToday, (o) => o.NfedhEmi),
          data: invoicedToday,
        };
      } catch (e) {
        console.error("Erro FATURAMENTO:", e);
        failures.push("FATURAMENTO");
      }

      // 3. EMBALAGEM ──────────────────────────────────────────────────────────
      try {
        const res = await apiEmbalei("/ranking/per-hour");
        if (res.data) {
          const { totals, orders } = res.data;
          nextPacking = {
            averageItems: totals.averageItems,
            averageOrders: totals.averageOrders,
            totalOrders: totals.total_orders,
            totalItems: totals.total_items,
            // O Embalei devolve hora numérica e total_itens/total_orders.
            // Normaliza pro mesmo formato do faturamento, pra as duas tabelas
            // hora-a-hora compartilharem o mesmo renderizador.
            hourByHour: (orders || []).map((row) => ({
              hour: `${String(row.hour).padStart(2, "0")}:00`,
              items: row.total_itens,
              orders: row.total_orders,
            })),
          };
        }
      } catch (e) {
        console.error("Erro EMBALAGEM:", e);
        failures.push("EMBALAGEM");
      }

      // 4. EMBARCADOS (enviados hoje) ─────────────────────────────────────────
      // Mesma consulta que antes alimentava a meta do dia; agora vira quadrante
      // próprio, com a distribuição hora a hora pela hora do envio.
      try {
        const data = await fetchOrdersPaged({
          ShippingDateFrom: todayISO,
          ShippingDateTo: todayISO,
          Budget: 0,
          IDOrderType: EXPEDITION_ORDER_TYPES,
        });
        nextShipped = {
          totalOrders: data.length,
          totalItems: sumItems(data),
          hourByHour: buildHourByHour(data, (o) => o.ShippingDate),
          data,
        };
      } catch (e) {
        console.error("Erro EMBARCADOS:", e);
        failures.push("EMBARCADOS");
      }


      // 5. AG. EMBALAGEM × AG. EMBARCAR ───────────────────────────────────────
      // As duas metades da mesma pergunta: dos pedidos em status 22, quais já
      // foram embalados. Embalado → espera embarque; não embalado → espera
      // embalagem. Único ponto lento da tela (ver splitByPacking).
      try {
        const status22 = ordersByStatus[22] || [];
        // Fila vazia também precisa ser publicada: sem isso os dois quadrantes
        // ficariam congelados no número do ciclo anterior.
        const { packed, notPacked } = status22.length
          ? await splitByPacking(status22, packedOrderIdsRef.current)
          : { packed: [], notPacked: [] };

        nextAwaitingPacking = {
          total: notPacked.length,
          items: sumItems(notPacked),
          data: notPacked,
        };
        nextAwaitingShipping = {
          total: packed.length,
          items: sumItems(packed),
          data: packed,
        };
      } catch (e) {
        console.error("Erro AG. EMBALAGEM / AG. EMBARCAR:", e);
        failures.push("EMBALAGEM_PENDENTE");
      }

      // 6. PUBLICAÇÃO ÚNICA ───────────────────────────────────────────────────
      // Todos os estados são aplicados de uma vez (batched), então o painel
      // troca por completo — nunca exibe dado parcial.
      if (cancelled) return;

      // Ciclo incompleto: publicar agora faria os quadrantes saírem errados
      // e os números "pularem". Mantém o que está na tela e tenta de novo
      // no próximo ciclo. (No primeiro carregamento publica assim mesmo, senão
      // o painel ficaria preso na tela de carregamento.)
      if (failures.length > 0 && firstLoadDone) {
        console.warn(
          `[ExpeditionV3] ciclo descartado — falhas: ${failures.join(", ")}. ` +
            "Mantendo os dados do ciclo anterior."
        );
        return;
      }
      firstLoadDone = true;

      unstable_batchedUpdates(() => {
        if (nextAwaiting) setAwaiting(nextAwaiting);
        if (nextAuthorized) setAuthorized(nextAuthorized);
        if (nextAwaitingLabel) setAwaitingLabel(nextAwaitingLabel);
        if (nextBilling) setBilling(nextBilling);
        if (nextPacking) setPacking(nextPacking);
        if (nextAwaitingPacking) setAwaitingPacking(nextAwaitingPacking);
        if (nextShipped) setShipped(nextShipped);
        if (nextAwaitingShipping) setAwaitingShipping(nextAwaitingShipping);
        setLoaded(true);
      });
    };

    // Um ciclo por vez: o splitByPacking é lento e pode passar do intervalo.
    const runCycle = async () => {
      if (running || cancelled) return;
      running = true;
      try {
        await fetchAll();
      } finally {
        running = false;
      }
    };

    runCycle();
    const id = setInterval(runCycle, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const style = useStyle();

  /**
   * Quadrante de etapa (FATURAMENTO / EMBALAGEM / EMBARCADOS):
   * tabela hora a hora + os quatro totais do lado.
   */
  const renderStage = ({ title, tone, rows, tabIndex, totalOrders, totalItems }) => {
    const activeHours = rows.length || 1;
    const forecast = buildForecast(rows, new Date().getHours());
    return (
      <div className={`${tone} ${style.stageBox}`}>
        <h1>{title}</h1>
        <section className={style.stageContent}>
          {renderHourTable(rows, tabIndex)}
          {/* Ordem: total, média, total, média, e as duas previsões no fim —
              realizado em cima, projeção embaixo. */}
          <div className={style.stageTotals}>
            <div className="total">
              <p className="label">TOTAL PEDIDOS</p>
              <p className="value">{totalOrders}</p>
            </div>
            <div className="total">
              <p className="label">MÉDIA PEDIDOS /H</p>
              <p className="value">{Math.round(totalOrders / activeHours)}</p>
            </div>
            <div className="total">
              <p className="label">TOTAL PEÇAS</p>
              <p className="value">{totalItems}</p>
            </div>
            <div className="total">
              <p className="label">MÉDIA PEÇAS /H</p>
              <p className="value">{Math.round(totalItems / activeHours)}</p>
            </div>
            <div className="total forecast">
              <p className="label">PREVISÃO PEDIDOS {forecast.label}</p>
              <p className="value">{forecast.orders}</p>
            </div>
            <div className="total forecast">
              <p className="label">PREVISÃO PEÇAS {forecast.label}</p>
              <p className="value">{forecast.items}</p>
            </div>
          </div>
        </section>
      </div>
    );
  };

  /**
   * Quadrante de fila (AG. FATURAMENTO / AG. EMBALAGEM / AG. EMBARCAR):
   * quantidades à esquerda, tabela de datas à direita.
   */
  const renderQueue = ({ title, tone, items, orders, history, onDayClick }) => (
    <div className={`${tone} ${style.queueBox}`}>
      <h1>{title}</h1>
      {/* Tabela à esquerda, números à direita — mesma leitura dos quadrantes
          grandes, pro olho não trocar de lado ao descer a coluna. */}
      <div className={style.queueContent}>
        <HistoryPanel
          data={history}
          styleHistory={style.history}
          onDayClick={onDayClick}
        />
        <div className={style.queueTotals}>
          <div><p className="label">QTDE PEÇAS</p><p className="value">{items}</p></div>
          <div><p className="label">QTDE PEDIDOS</p><p className="value">{orders}</p></div>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  // Primeiro ciclo ainda rodando: não mostra o painel com os quadrantes zerados
  if (!loaded) {
    return (
      <section className={style.fullWidth}>
        <div className={style.loadingScreen}>
          <div className={style.loadingSpinner} />
          <p>Carregando dados da operação…</p>
        </div>
      </section>
    );
  }

  return (
    <section className={style.fullWidth}>

      {/* Coluna 1 — status, empilhados */}
      <div className={style.statusColumn}>
        <div className={`${style.cardWaitLabel} ${style.statusCard}`}>
          <h1>ESP. ETIQUETA</h1>
          <div className="values">
            <div><p className="label">QTDE PEÇAS</p><p className="value">{awaitingLabel.items}</p></div>
            <div><p className="label">QTDE PEDIDOS</p><p className="value">{awaitingLabel.total}</p></div>
          </div>
        </div>
        <div className={`${style.cardAgInicio} ${style.statusCard}`}>
          <h1>AG. INÍCIO</h1>
          <div className="values">
            <div><p className="label">QTDE PEÇAS</p><p className="value">{awaiting.items}</p></div>
            <div><p className="label">QTDE PEDIDOS</p><p className="value">{awaiting.total}</p></div>
          </div>
        </div>
        <div className={`${style.cardAuthorized} ${style.statusCard}`}>
          <h1>AUTORIZADO</h1>
          <div className="values">
            <div><p className="label">QTDE PEÇAS</p><p className="value">{authorized.items}</p></div>
            <div><p className="label">QTDE PEDIDOS</p><p className="value">{authorized.total}</p></div>
          </div>
        </div>
      </div>

      {/* Colunas 2 a 4 — etapa em cima, fila embaixo */}
      <div className={style.stageColumn}>
        {renderStage({
          title: "FATURAMENTO",
          tone: style.boxBilling,
          rows: billing.hourByHour,
          tabIndex: billingHourTab,
          totalOrders: billing.totalOrders,
          totalItems: billing.totalItems,
        })}
        {renderQueue({
          title: "AG. FATURAMENTO",
          tone: style.boxAgBilling,
          items: authorized.items + awaiting.items,
          orders: authorized.total + awaiting.total,
          history: historyBillingAwaiting,
          onDayClick: (dayKey) =>
            openDayModal(
              "billing",
              `AG. FATURAMENTO — ${dayKey}`,
              ordersForDay([...authorized.data, ...awaiting.data], dayKey)
            ),
        })}
      </div>

      <div className={style.stageColumn}>
        {renderStage({
          title: "EMBALAGEM",
          tone: style.boxPackaging,
          rows: packing.hourByHour,
          tabIndex: packingHourTab,
          totalOrders: packing.totalOrders,
          totalItems: packing.totalItems,
        })}
        {renderQueue({
          title: "AG. EMBALAGEM",
          tone: style.boxAgPacking,
          items: awaitingPacking.items,
          orders: awaitingPacking.total,
          history: historyPackingAwaiting,
          onDayClick: (dayKey) =>
            openDayModal(
              "packing",
              `AG. EMBALAGEM — ${dayKey}`,
              ordersForDay(awaitingPacking.data, dayKey)
            ),
        })}
      </div>

      <div className={style.stageColumn}>
        {renderStage({
          title: "EMBARCADOS",
          tone: style.boxShipped,
          rows: shipped.hourByHour,
          tabIndex: shippedHourTab,
          totalOrders: shipped.totalOrders,
          totalItems: shipped.totalItems,
        })}
        {renderQueue({
          title: "AG. EMBARCAR",
          tone: style.boxAgShipping,
          items: awaitingShipping.items,
          orders: awaitingShipping.total,
          history: historyAwaitingShipping,
          onDayClick: (dayKey) =>
            openDayModal(
              "packing",
              `AG. EMBARCAR — ${dayKey}`,
              ordersForDay(awaitingShipping.data, dayKey)
            ),
        })}
      </div>


      <HistoryOrdersModal modal={historyModal} onClose={closeDayModal} />

    </section>
  );
}

export default ExpeditionV3;
